import { createMemo, createSignal, For, Show, type Accessor } from "solid-js";
import Text from "../Text";
import { useTranslation } from "../../hooks/useTranslation";
import type { StorageInfo } from "../../services/inventoryApi";
import styles from "./StorageList.module.css";

interface Props {
  storages: Accessor<StorageInfo[]>;
  selectedId: Accessor<string | null>;
  onSelect: (id: string) => void;
}

export default function StorageList(props: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = createSignal("");

  const filtered = createMemo(() => {
    const q = search().trim().toLowerCase();
    const list = props.storages();
    if (!q) return list;
    return list.filter(s =>
      s.name.toLowerCase().includes(q)
      || (s.citybuildName ?? "").toLowerCase().includes(q)
      || (s.location ?? "").toLowerCase().includes(q)
      || (s.ownerName ?? "").toLowerCase().includes(q));
  });

  const owned = createMemo(() => filtered().filter(s => s.isOwner));
  const shared = createMemo(() => filtered().filter(s => !s.isOwner));

  function row(s: StorageInfo) {
    return (
      <div
        class={`${styles.srow} ${props.selectedId() === s.id ? styles.srowActive : ""}`}
        onClick={() => props.onSelect(s.id)}
      >
        <i class={`fa-solid ${s.isOwner ? "fa-box" : "fa-share-nodes"} ${styles.srowLead}`} />
        <div class={styles.srowText}>
          <div class={styles.srowName}>{s.name}</div>
          <div class={styles.srowMeta}>
            {s.isOwner ? "" : `${s.ownerName} · `}
            {s.citybuildName}
            {s.location ? ` · ${s.location}` : ""}
          </div>
        </div>
        <span class={styles.srowCount}>{s.chestCount}</span>
      </div>
    );
  }

  return (
    <div class={styles.rail}>
      <div class={styles.railSearch}>
        <i class="fa-solid fa-magnifying-glass" />
        <input
          value={search()}
          onInput={e => setSearch(e.currentTarget.value)}
          placeholder={t('STORAGE_SEARCH_STORAGES')}
        />
      </div>

      <Show when={props.storages().length === 0}>
        <div class={styles.railEmpty}>
          <i class="fa-solid fa-box-open" />
          <p><Text val="STORAGE_EMPTY" /></p>
        </div>
      </Show>
      <Show when={props.storages().length > 0 && filtered().length === 0}>
        <div class={styles.railEmpty}>
          <i class="fa-solid fa-magnifying-glass" />
          <p><Text val="STORAGE_NO_MATCH" /></p>
        </div>
      </Show>

      <Show when={owned().length > 0}>
        <div class={styles.sectionTitle}><Text val="STORAGE_SECTION_OWNED" /></div>
        <For each={owned()}>{row}</For>
      </Show>

      <Show when={shared().length > 0}>
        <div class={styles.sectionTitle}><Text val="STORAGE_SECTION_SHARED" /></div>
        <For each={shared()}>{row}</For>
      </Show>
    </div>
  );
}
